import { useState } from 'react';
import { useSeo } from '../hooks/useSeo';

type ApiRow = {
  name: string;
  type: string;
  description: string;
};

type CodeBlockProps = {
  code: string;
  language?: string;
};

function CodeBlock({ code, language = 'bash' }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code.trim());
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{ position: 'relative', marginTop: 12, marginBottom: 20 }}>
      <button onClick={handleCopy} style={styles.copyBtn}>
        {copied ? 'Copied' : 'Copy'}
      </button>
      <div style={styles.langLabel}>{language}</div>
      <pre style={styles.codePre}>
        <code>{code.trim()}</code>
      </pre>
    </div>
  );
}

function ApiTable({ rows }: { rows: ApiRow[] }) {
  return (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name}>
              <td style={styles.td}><code>{row.name}</code></td>
              <td style={styles.td}>{row.type}</td>
              <td style={styles.td}>{row.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ApiReference() {
  useSeo('API Reference — Media Magic', 'Full API reference for Media Magic image transforms, upload endpoints, and analytics.');
  const [tab, setTab] = useState<'react' | 'python'>('react');

  return (
    <div className="portal-surface" style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px 44px' }}>
      <h1 style={{ fontSize: 34, marginBottom: 8, color: 'var(--ink-950)' }}>API Reference</h1>
      <p style={{ color: 'var(--ink-600)', marginBottom: 24, lineHeight: 1.6 }}>
        Complete documentation for all components, hooks, utilities, and types in Media Magic.
      </p>

      <div style={styles.tabContainer}>
        <button
          onClick={() => setTab('react')}
          style={{
            ...styles.tabBtn,
            ...(tab === 'react' ? styles.tabBtnActive : styles.tabBtnInactive),
          }}
        >
          React SDK
        </button>
        <button
          onClick={() => setTab('python')}
          style={{
            ...styles.tabBtn,
            ...(tab === 'python' ? styles.tabBtnActive : styles.tabBtnInactive),
          }}
        >
          Python Package
        </button>
      </div>

      {tab === 'react' && (
        <div>
          <section style={styles.section}>
            <h2 style={styles.h2}>Components</h2>

            <h3 style={styles.h3}>MagicImageMin</h3>
            <p style={styles.p}>Lightweight wrapper around an HTML <code>&lt;img&gt;</code> tag. Use for simple, non-responsive images.</p>
            <ApiTable
              rows={[
                { name: 'src', type: 'string', description: 'Path to image asset (e.g., /assets/photo.jpg).' },
                { name: 'alt', type: 'string', description: 'Accessible image alt text.' },
                { name: 'width', type: 'number', description: 'Image width in pixels.' },
                { name: 'height', type: 'number', description: 'Image height in pixels.' },
                { name: 'crop', type: 'boolean', description: 'Crop to exact width/height instead of simple resize.' },
                { name: 'autofocus', type: "boolean | 'smart' | 'face' | 'object' | 'primary'", description: 'Autofocus mode for smart cropping.' },
                { name: 'quality', type: 'number', description: 'Output quality control (lower = smaller files).' },
              ]}
            />

            <h3 style={styles.h3}>MagicImage</h3>
            <p style={styles.p}>Full-featured image component with blur placeholder, layout modes, and responsive sizing.</p>
            <ApiTable
              rows={[
                { name: 'src', type: 'string', description: 'Path to image asset.' },
                { name: 'alt', type: 'string', description: 'Accessible image alt text.' },
                { name: 'width, height', type: 'number', description: 'Required for layout="fixed". Optional for responsive flows.' },
                { name: 'layout', type: "'intrinsic' | 'responsive' | 'fill' | 'fixed'", description: 'Layout mode: intrinsic (natural dims), responsive (fluid width), fill (absolute), fixed (strict pixels).' },
                { name: 'crop', type: 'boolean', description: 'Crop to exact dimensions instead of resize.' },
                { name: 'autofocus', type: "boolean | 'smart' | 'face' | 'object' | 'primary'", description: 'Smart cropping mode.' },
                { name: 'quality', type: 'number', description: 'Quality control.' },
                { name: 'autoSize', type: 'boolean', description: 'Observe container and request optimized widths automatically.' },
                { name: 'aspectRatio', type: 'string', description: 'Aspect ratio hint like "16 / 10" for responsive stability.' },
              ]}
            />

            <h3 style={styles.h3}>Picture</h3>
            <p style={styles.p}>Responsive <code>&lt;picture&gt;</code> element with multiple sources and breakpoints.</p>
            <ApiTable
              rows={[
                { name: 'alt', type: 'string', description: 'Accessible alt text for the fallback img.' },
                { name: 'sources', type: 'PictureSource[]', description: 'Array of picture sources with media queries and sizes.' },
                { name: 'imgProps', type: 'Partial<MagicImageMinProps>', description: 'Props passed to the fallback <img> element.' },
              ]}
            />
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Providers</h2>

            <h3 style={styles.h3}>DevProvider</h3>
            <p style={styles.p}>Wraps your app in development mode. Enables path-to-mediaId resolution via dev API.</p>
            <ApiTable
              rows={[
                { name: 'config', type: 'DevConfig', description: 'Dev configuration object.' },
                { name: 'config.apiUrl', type: 'string', description: 'Backend API endpoint URL for dev resolve + upload flow.' },
                { name: 'config.resolveEndpoint', type: 'string', description: 'Dev middleware endpoint (default: /__media_magic/resolve).' },
                { name: 'config.sdkConfig.clientId', type: 'string', description: 'Tenant/user client ID for generated CDN URLs.' },
                { name: 'config.sdkConfig.cdnUrl', type: 'string', description: 'CloudFront/CDN base URL for image rendering.' },
              ]}
            />

            <h3 style={styles.h3}>MediaMagicProvider</h3>
            <p style={styles.p}>Production provider. Use in your published app with real mediaIds and CDN URLs.</p>
            <ApiTable
              rows={[
                { name: 'config', type: 'MediaMagicConfig', description: 'Production configuration.' },
                { name: 'config.clientId', type: 'string', description: 'Tenant/user client ID.' },
                { name: 'config.cdnUrl', type: 'string', description: 'CDN base URL for image rendering.' },
              ]}
            />

            <h3 style={styles.h3}>StatsProvider</h3>
            <p style={styles.p}>Collects and displays image transformation statistics. Wraps app at root level.</p>
            <ApiTable
              rows={[
                { name: 'children', type: 'ReactNode', description: 'App content.' },
              ]}
            />
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Stats Components</h2>

            <h3 style={styles.h3}>StatsOverlay</h3>
            <p style={styles.p}>Per-image stats popup showing original and optimized dimensions, file sizes, and reduction metrics.</p>

            <h3 style={styles.h3}>GlobalStatsDashboard</h3>
            <p style={styles.p}>Aggregate dashboard showing total images, combined savings, cache hit ratio, and performance metrics. Auto-opens with Cmd+B (Mac) or Ctrl+B (Linux/Windows) when StatsProvider is active.</p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Hooks</h2>

            <h3 style={styles.h3}>useMediaMagic()</h3>
            <p style={styles.p}>Access MediaMagicConfig in production context.</p>
            <CodeBlock
              language="ts"
              code={`const config = useMediaMagic();
// Returns: { clientId: string, cdnUrl: string }`}
            />

            <h3 style={styles.h3}>useMediaMagicDev()</h3>
            <p style={styles.p}>Access DevConfig in dev context. Also returns <code>invalidateMediaId(mediaId)</code> and <code>clearDevCaches()</code>.</p>
            <CodeBlock
              language="ts"
              code={`const { config, invalidateMediaId, clearDevCaches } = useMediaMagicDev();`}
            />

            <h3 style={styles.h3}>useContainerSize()</h3>
            <p style={styles.p}>Hook for responsive container width tracking. Returns <code>{`{ width, height }`}</code>.</p>

            <h3 style={styles.h3}>useStatsOverlay()</h3>
            <p style={styles.p}>Access stats context and keyboard binding state. Used internally by stats components.</p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Utilities</h2>

            <h3 style={styles.h3}>buildUrl(mediaId, options)</h3>
            <p style={styles.p}>Build a CDN image URL from a media_id with optional transform options.</p>
            <CodeBlock
              language="ts"
              code={`const url = buildUrl('media_123', {
  width: 800,
  height: 600,
  crop: true,
  autofocus: 'smart',
  quality: 85,
});`}
            />

            <h3 style={styles.h3}>buildSrcSet(mediaId, widths)</h3>
            <p style={styles.p}>Generate a responsive <code>srcset</code> string for multiple device widths.</p>

            <h3 style={styles.h3}>buildBlurUrl(mediaId)</h3>
            <p style={styles.p}>Generate a low-quality placeholder URL for progressive image loading.</p>

            <h3 style={styles.h3}>formatBytes(bytes)</h3>
            <p style={styles.p}>Format byte size to human-readable string (e.g., "2.5 MB").</p>

            <h3 style={styles.h3}>calculateReductionPercent(original, optimized)</h3>
            <p style={styles.p}>Calculate file size reduction percentage.</p>

            <h3 style={styles.h3}>calculateAggregates(imageStatsArray)</h3>
            <p style={styles.p}>Compute aggregate statistics across multiple images.</p>

            <h3 style={styles.h3}>parseStatsHeaders(headers)</h3>
            <p style={styles.p}>Parse X-MM-* response headers into an <code>ImageStats</code> object.</p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Types</h2>
            <ApiTable
              rows={[
                { name: 'MediaMagicConfig', type: 'type', description: 'Production SDK configuration.' },
                { name: 'DevConfig', type: 'type', description: 'Development SDK configuration.' },
                { name: 'TransformOptions', type: 'type', description: 'Image transformation parameters.' },
                { name: 'LayoutMode', type: 'type', description: 'Layout mode union: "intrinsic" | "responsive" | "fill" | "fixed".' },
                { name: 'MagicImageProps', type: 'type', description: 'Props for MagicImage component.' },
                { name: 'MagicImageMinProps', type: 'type', description: 'Props for MagicImageMin component.' },
                { name: 'PictureProps', type: 'type', description: 'Props for Picture component.' },
                { name: 'ImageStats', type: 'type', description: 'Single-image statistics from transform.' },
                { name: 'AggregateStats', type: 'type', description: 'Aggregate statistics across images.' },
              ]}
            />
          </section>
        </div>
      )}

      {tab === 'python' && (
        <div>
          <section style={styles.section}>
            <h2 style={styles.h2}>Module-Level API</h2>
            <p style={styles.p}>
              The Python package exports a simple, module-level API for integration with FastAPI applications.
            </p>

            <ApiTable
              rows={[
                { name: 'setup()', type: 'async () → SetupResult', description: 'Initialize the resolver singleton from environment variables (MM_API_ENDPOINT, MM_API_KEY, MM_CLIENT_ID, MM_CDN_URL).' },
                { name: 'register()', type: '(name, resolve_local_path?, resolve_revalidate_target?) → None', description: 'Register a named media profile with optional path resolution callbacks.' },
                { name: 'resolve()', type: 'async (name, file_path, media_magic_key?) → str | None', description: 'Resolve a local file path to a media_id using the named profile.' },
                { name: 'revalidate()', type: 'async (name, file_path, old_media_id?, media_magic_key?) → str | None', description: 'Force-refresh a media_id by revalidating the source file.' },
                { name: 'revalidate_media_id()', type: 'async (name, media_id, context?) → str | None', description: 'Revalidate by stale media_id using profile callback.' },
                { name: 'replace_stale_media_id()', type: '(media_id) → str | None', description: 'Sync lookup of stale→fresh media_id mapping.' },
                { name: 'create_revalidate_router()', type: '(default_resolver?, context_dependency?) → APIRouter', description: 'FastAPI router factory exposing POST /revalidate-media-id.' },
                { name: 'get_resolver()', type: '() → MediaMagicResolver | None', description: 'Access the module-level resolver singleton (or None if setup() not called).' },
              ]}
            />
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Models</h2>

            <h3 style={styles.h3}>ResolverConfig</h3>
            <p style={styles.p}>Configuration for the resolver singleton. Auto-loads from environment or pass explicitly to <code>setup()</code>.</p>
            <ApiTable
              rows={[
                { name: 'api_endpoint', type: 'str', description: 'Media Magic API base URL (from MM_API_ENDPOINT env).' },
                { name: 'api_key', type: 'str', description: 'API key for authentication (from MM_API_KEY env).' },
                { name: 'client_id', type: 'str', description: 'Tenant/user ID (from MM_CLIENT_ID env).' },
                { name: 'cdn_url', type: 'str', description: 'CDN base URL for image rendering (from MM_CDN_URL env).' },
              ]}
            />

            <h3 style={styles.h3}>ResolveResult</h3>
            <p style={styles.p}>Result of a resolve operation.</p>
            <ApiTable
              rows={[
                { name: 'media_id', type: 'str', description: 'The resolved media_id.' },
                { name: 'is_cached', type: 'bool', description: 'True if result came from local cache.' },
              ]}
            />

            <h3 style={styles.h3}>TransformOptions</h3>
            <p style={styles.p}>Image transformation parameters for <code>image_url()</code>.</p>
            <ApiTable
              rows={[
                { name: 'width', type: 'int | None', description: 'Resize to width (pixels).' },
                { name: 'height', type: 'int | None', description: 'Resize to height (pixels).' },
                { name: 'crop', type: 'bool', description: 'Crop to exact dimensions.' },
                { name: 'quality', type: 'int | None', description: 'Output quality (0-100).' },
              ]}
            />

            <h3 style={styles.h3}>MediaProfile</h3>
            <p style={styles.p}>Named profile registered via <code>register()</code>.</p>
            <ApiTable
              rows={[
                { name: 'name', type: 'str', description: 'Profile name.' },
                { name: 'resolve_local_path', type: 'Callable', description: 'Function to map file path → file system path.' },
                { name: 'resolve_revalidate_target', type: 'Callable | None', description: 'Optional: callback to map stale media_id → source path.' },
              ]}
            />

            <h3 style={styles.h3}>SetupResult</h3>
            <p style={styles.p}>Result of <code>setup()</code> call.</p>
            <ApiTable
              rows={[
                { name: 'client_id', type: 'str', description: 'Initialized client_id.' },
                { name: 'pulled_cache_entries', type: 'int', description: 'Number of cache entries pulled from remote.' },
              ]}
            />
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Advanced: MediaMagicResolver Class</h2>
            <p style={styles.p}>
              Low-level resolver class (returned by <code>get_resolver()</code>). Use the module-level functions above in most cases.
            </p>

            <h3 style={styles.h3}>Methods</h3>
            <ApiTable
              rows={[
                { name: 'get_media_id()', type: 'async (file_path, media_magic_key?, resolve_local_path?, force_resolve?) → str | None', description: 'Core resolve operation.' },
                { name: 'image_url()', type: '(media_id, transforms?) → str', description: 'Build CDN URL from media_id + optional transforms.' },
                { name: 'invalidate_media_id()', type: '(media_id, media_magic_key?) → None', description: 'Mark a media_id as stale.' },
                { name: 'track_media_replacement()', type: '(old_id, new_id) → None', description: 'Record old→new media_id mapping.' },
                { name: 'resolve_latest_media_id()', type: '(media_id) → str', description: 'Look up fresh media_id from stale ID.' },
              ]}
            />
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Minimal FastAPI Example</h2>
            <CodeBlock
              language="python"
              code={`from contextlib import asynccontextmanager
from fastapi import FastAPI
import media_magic_fastapi

# Register a profile to map file paths
media_magic_fastapi.register(
    'images',
    resolve_local_path=lambda path: f'./static/images/{path}',
)

@asynccontextmanager
async def lifespan(app: FastAPI):
    # Startup: Initialize resolver
    await media_magic_fastapi.setup()
    yield
    # Shutdown

app = FastAPI(lifespan=lifespan)

# Include revalidation endpoint
app.include_router(
    media_magic_fastapi.create_revalidate_router(default_resolver='images')
)

# Use in responses
@app.get('/image/{file_path:path}')
async def get_image(file_path: str):
    media_id = await media_magic_fastapi.resolve('images', file_path)
    if not media_id:
        return {'error': 'Could not resolve image'}
    cdn_url = media_magic_fastapi.get_resolver().image_url(media_id)
    return {'url': cdn_url}`}
            />
          </section>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    backgroundColor: 'transparent',
    border: '1px solid var(--line-soft)',
    borderRadius: 12,
    padding: 28,
    marginBottom: 20,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  h2: {
    fontSize: 24,
    color: 'var(--ink-950)',
    marginBottom: 12,
    fontWeight: 700,
  },
  h3: {
    fontSize: 16,
    marginTop: 20,
    marginBottom: 8,
    color: 'var(--ink-950)',
    fontWeight: 600,
  },
  p: {
    color: 'var(--ink-600)',
    lineHeight: 1.7,
    fontSize: 15,
  },
  tabContainer: {
    display: 'flex',
    gap: 12,
    marginBottom: 32,
    borderBottom: '1px solid var(--line-soft)',
    paddingBottom: 16,
  },
  tabBtn: {
    padding: '12px 20px',
    borderRadius: 0,
    border: 'none',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s',
    borderBottom: '2px solid transparent',
    background: 'transparent',
  },
  tabBtnActive: {
    color: 'var(--primary-600)',
    borderBottomColor: 'var(--primary-600)',
  },
  tabBtnInactive: {
    color: 'var(--ink-500)',
  },
  copyBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    border: '1px solid var(--line-soft)',
    backgroundColor: 'var(--surface-2)',
    color: 'var(--ink-700)',
    borderRadius: 6,
    padding: '6px 12px',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  langLabel: {
    position: 'absolute',
    top: 10,
    left: 12,
    color: 'var(--ink-500)',
    fontFamily: 'monospace',
    fontSize: 11,
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  codePre: {
    margin: 0,
    overflowX: 'auto',
    backgroundColor: 'var(--ink-950)',
    color: 'var(--surface-0)',
    borderRadius: 10,
    padding: '36px 16px 16px',
    border: '1px solid var(--ink-900)',
    fontSize: 13,
    lineHeight: 1.6,
  },
  th: {
    textAlign: 'left',
    padding: '12px 14px',
    borderBottom: '1px solid var(--line-soft)',
    color: 'var(--ink-950)',
    fontWeight: 600,
  },
  td: {
    padding: '12px 14px',
    borderBottom: '1px solid var(--line-soft)',
    color: 'var(--ink-700)',
    verticalAlign: 'top',
  },
};

export default ApiReference;
